import { useEffect } from 'react';
import { messageService } from '../services/messageService';
import { useSocket } from '../contexts/SocketContext';
import { SOCKET_EVENT_NAME } from '../constants/constants';
import { MSG_USER_ROLE } from '../utils/messageHelpers';

export const useMessages = (chatState, setChatState, isLoadingRef, messagesScrollRef) => {
  const { emit, isConnected } = useSocket();
  const fetchMessages = async (candidateId, page = 1, fromFirstUnread = false) => {
    try {
      return await messageService.fetchMessages(candidateId, page, fromFirstUnread);
    } catch (error) {
      console.error('Error fetching messages:', error);
      return null;
    }
  };

  const scrollToMessage = (messageId, scrollType = 'instant') => {
    setTimeout(() => {
      const container = messagesScrollRef.current;
      const messageElement = document.querySelector(`[data-newmsg-id="divider-${messageId}"]`);
      if (container && messageElement) {
        const containerRect = container.getBoundingClientRect();
        const messageRect = messageElement.getBoundingClientRect();

        const scrollTop =
          container.scrollTop +
          messageRect.top -
          containerRect.top -
          containerRect.height / 2 +
          messageRect.height / 2;

        container.scrollTo({
          top: scrollTop,
          behavior: scrollType
        });
      }
    }, 200);
  };

  const scrollToBottom = (scrollType = 'instant') => {
    setTimeout(() => {
      const container = messagesScrollRef.current;
      scrollType = typeof scrollType === 'string' ? scrollType : 'instant';
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: scrollType
        });
      }
    }, 100);
  };
  useEffect(() => {
    const container = messagesScrollRef.current;
    if (container) {
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      if (isNearBottom) {
        scrollToBottom('smooth');
      }
    }
  }, [chatState.currentMessages]);

  const loadInitialMessages = async (candidateId, isSmallChat = false) => {
    setChatState((prev) => ({ ...prev, isLoading: true, currentPage: 1 }));
    isLoadingRef.current = true;
    try {
      const data = await fetchMessages(candidateId, 1, true);
      if (data?.data) {
        if (isConnected && (chatState?.unreadCounts?.[candidateId] > 0 || isSmallChat)) {
          emit(SOCKET_EVENT_NAME.MARK_CHAT_AS_READ, {
            senderId: candidateId,
            senderType: MSG_USER_ROLE.CANDIDATE
          });
        }
        setChatState((prev) => ({
          ...prev,
          currentMessages: data.data.messages,
          currentPage: data.data.pagination.page,
          firstUnreadMessageId: data.data.pagination.firstUnreadMessageId,
          canLoadMore: data.data.pagination.hasMore,
          isLoading: false
        }));
        if (data.data?.pagination?.firstUnreadMessageId) {
          scrollToMessage(data.data?.pagination?.firstUnreadMessageId);
        } else {
          scrollToBottom();
        }
      } else {
        setChatState((prev) => ({ ...prev, isLoading: false, currentMessages: [] }));
      }
      isLoadingRef.current = false;
    } catch (error) {
      setChatState((prev) => ({ ...prev, isLoading: false }));
      isLoadingRef.current = false;
    }
  };

  const loadPreviousMessages = async () => {
    if (isLoadingRef.current || !chatState.canLoadMore || !chatState.activeChatId) return;

    const scrollContainer = messagesScrollRef.current;
    if (!scrollContainer) return;

    const previousScrollHeight = scrollContainer.scrollHeight;
    const previousScrollTop = scrollContainer.scrollTop;

    isLoadingRef.current = true;
    setChatState((prev) => ({ ...prev, isLoading: true }));

    try {
      const nextPage = chatState.currentPage + 1;
      const data = await fetchMessages(chatState.activeChatId, nextPage);

      if (data?.data?.messages && data.data.messages.length > 0) {
        const { flushSync } = await import('react-dom');

        flushSync(() => {
          setChatState((prev) => ({
            ...prev,
            currentMessages: [...data.data.messages, ...prev.currentMessages],
            canLoadMore: data.data.pagination.hasMore,
            currentPage: nextPage,
            isLoading: false
          }));
        });

        requestAnimationFrame(() => {
          if (scrollContainer) {
            const newScrollHeight = scrollContainer.scrollHeight;
            const heightDifference = newScrollHeight - previousScrollHeight;
            const newScrollTop = previousScrollTop + heightDifference + 10;

            scrollContainer.scrollTo({
              top: newScrollTop,
              behavior: 'instant'
            });
          }
          isLoadingRef.current = false;
        });
      } else {
        setChatState((prev) => ({ ...prev, canLoadMore: false, isLoading: false }));
        isLoadingRef.current = false;
      }
    } catch (error) {
      console.error('Error loading previous messages:', error);
      setChatState((prev) => ({ ...prev, isLoading: false }));
      isLoadingRef.current = false;
    }
  };

  return {
    loadInitialMessages,
    loadPreviousMessages,
    scrollToBottom
  };
};
